import { useNotifications } from '@tsyl/zapan2'
import { onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

type NavigatorWithConnection = Navigator & {
  connection?: {
    effectiveType?: string
    saveData?: boolean
    addEventListener: (type: string, listener: () => void) => void
    removeEventListener: (type: string, listener: () => void) => void
  }
}

const SLOW_TYPES = new Set(['slow-2g', '2g'])

export function useNetworkNotifications() {
  const { t } = useI18n()
  const { addNotification } = useNotifications()

  let wasSlowNetwork = false

  const handleOffline = () => {
    addNotification({
      type: 'error',
      title: t('networkStatus.offlineTitle'),
      body: t('networkStatus.offline'),
    })
  }

  const handleOnline = () => {
    addNotification({
      type: 'info',
      title: t('networkStatus.onlineTitle'),
      body: t('networkStatus.online'),
    })
  }

  const handleNetworkChange = () => {
    const conn = (navigator as NavigatorWithConnection).connection
    if (!conn) return
    const isSlow = SLOW_TYPES.has(conn.effectiveType ?? '') || !!conn.saveData
    if (isSlow && !wasSlowNetwork) {
      addNotification({
        type: 'warning',
        title: t('networkStatus.slowTitle'),
        body: t('networkStatus.slow'),
      })
      wasSlowNetwork = true
    } else if (!isSlow) {
      wasSlowNetwork = false
    }
  }

  onMounted(() => {
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    const conn = (navigator as NavigatorWithConnection).connection
    if (conn) {
      handleNetworkChange()
      conn.addEventListener('change', handleNetworkChange)
    }
  })

  onUnmounted(() => {
    window.removeEventListener('offline', handleOffline)
    window.removeEventListener('online', handleOnline)
    ;(navigator as NavigatorWithConnection).connection?.removeEventListener(
      'change',
      handleNetworkChange,
    )
  })

  return { handleOffline, handleOnline, handleNetworkChange }
}
